import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CBadge,
  CButton,
  CCloseButton,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CLoadingButton,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilArrowRight, cilPeople, cilPlus, cilSearch } from '@coreui/icons'

import GradeService from 'src/service/GradeService'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'

const API_URL = process.env.REACT_APP_API_URL
const SEARCH_THRESHOLD = 6

const TeamAvatar = ({ team }) => {
  const src = team.image
    ? `${API_URL}uploads/team_sponsor/${team.image}`
    : team.game_logo
    ? `${API_URL}uploads/game_logo/${team.game_logo}`
    : null

  if (src) {
    return (
      <img
        src={src}
        alt=""
        width="36"
        height="36"
        className="rounded flex-shrink-0"
        style={{ objectFit: 'cover' }}
      />
    )
  }
  return (
    <div
      className="rounded bg-primary-subtle text-primary fw-semibold d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 36, height: 36 }}
    >
      {(team.team_name || '?').charAt(0).toUpperCase()}
    </div>
  )
}

// Teams that play in one comp, shown inside the Edit Comp sidebar. Lists them and offers a
// quick add (name only - the comp is implied); logo, sponsor and owner are set from Teams.
const GradeTeams = ({ gradeId, visible, onTeamsChanged }) => {
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [teamName, setTeamName] = useState('')
  const [addError, setAddError] = useState('')
  const [saving, setSaving] = useState(false)
  const [newIds, setNewIds] = useState([])
  const inputRef = useRef(null)

  const loadTeams = useCallback(
    (showSpinner = true) => {
      if (!gradeId) return Promise.resolve()
      if (showSpinner) setLoading(true)
      return GradeService.getGradeTeams(gradeId)
        .then((res) => {
          if (res.status === 200) {
            setTeams(res.data || [])
          } else {
            ToastComponent(res.message || 'Unable to load teams.', 'error')
          }
        })
        .catch(() => ToastComponent('Unable to load teams.', 'error'))
        .finally(() => setLoading(false))
    },
    [gradeId],
  )

  useEffect(() => {
    if (!visible || !gradeId) return
    setTeams([])
    setSearch('')
    setAdding(false)
    setTeamName('')
    setAddError('')
    setNewIds([])
    loadTeams()
  }, [visible, gradeId, loadTeams])

  useEffect(() => {
    if (adding) inputRef.current?.focus()
  }, [adding])

  const filteredTeams = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return teams
    return teams.filter(
      (team) =>
        team.team_name?.toLowerCase().includes(term) || team.sn?.toLowerCase().includes(term),
    )
  }, [teams, search])

  const closeQuickAdd = () => {
    setAdding(false)
    setTeamName('')
    setAddError('')
  }

  const addTeam = () => {
    const name = teamName.trim()
    if (!name) {
      setAddError('Team name is required')
      return
    }
    if (name.length > 50) {
      setAddError('50 Character Limit is allowed.')
      return
    }

    const formData = new FormData()
    formData.append('name', name)
    formData.append('grade_name', gradeId)

    setSaving(true)
    TeamService.saveTeam(formData)
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          setTeamName('')
          setAddError('')
          if (res.data?.id) setNewIds((ids) => [...ids, res.data.id])
          loadTeams(false)
          onTeamsChanged && onTeamsChanged()
          inputRef.current?.focus()
        } else {
          const errors =
            res.errors && Array.isArray(res.errors)
              ? res.errors
              : [res.message || 'Something went wrong.']
          setAddError(errors.join(' '))
        }
      })
      .catch(() => setAddError('Something went wrong. Please try again.'))
      .finally(() => setSaving(false))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTeam()
    } else if (e.key === 'Escape') {
      e.stopPropagation()
      closeQuickAdd()
    }
  }

  return (
    <div className="border-top pt-3 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex align-items-center gap-2">
          <h6 className="fw-bold mb-0">Teams</h6>
          <CBadge color={teams.length ? 'primary' : 'secondary'} shape="rounded-pill">
            {teams.length}
          </CBadge>
        </div>
        {!adding && teams.length > 0 && (
          <CButton color="primary" variant="outline" size="sm" onClick={() => setAdding(true)}>
            <CIcon icon={cilPlus} className="me-1" />
            Add Team
          </CButton>
        )}
      </div>
      <div className="text-medium-emphasis small mb-3">Teams that play in this comp.</div>

      {adding && (
        <div className="rounded border bg-body-tertiary p-2 mb-3">
          <div className="d-flex gap-2 align-items-start">
            <div className="flex-grow-1">
              <CFormInput
                ref={inputRef}
                size="sm"
                placeholder="Team name"
                value={teamName}
                invalid={!!addError}
                disabled={saving}
                onChange={(e) => {
                  setTeamName(e.target.value)
                  if (addError) setAddError('')
                }}
                onKeyDown={handleKeyDown}
                aria-label="New team name"
              />
            </div>
            <CLoadingButton color="primary" size="sm" loading={saving} onClick={addTeam}>
              Add
            </CLoadingButton>
            <CCloseButton className="mt-1" onClick={closeQuickAdd} aria-label="Cancel" />
          </div>
          {addError ? (
            <div className="text-danger small mt-1">{addError}</div>
          ) : (
            <div className="text-medium-emphasis small mt-1">
              Press Enter to add, Esc to close. Logo, sponsor &amp; owner can be added from Teams.
            </div>
          )}
        </div>
      )}

      {teams.length >= SEARCH_THRESHOLD && (
        <CInputGroup size="sm" className="mb-2">
          <CInputGroupText>
            <CIcon icon={cilSearch} />
          </CInputGroupText>
          <CFormInput
            placeholder="Search teams"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search teams"
          />
        </CInputGroup>
      )}

      {loading ? (
        <div className="text-center py-4">
          <CSpinner size="sm" color="primary" />
        </div>
      ) : teams.length === 0 ? (
        !adding && (
          <div className="text-center rounded border py-4 px-3" style={{ borderStyle: 'dashed' }}>
            <div
              className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary mb-2"
              style={{ width: 42, height: 42 }}
            >
              <CIcon icon={cilPeople} size="lg" />
            </div>
            <div className="fw-semibold">No teams yet</div>
            <div className="text-medium-emphasis small mb-3">
              Add the teams that play in this comp.
            </div>
            <CButton color="primary" size="sm" onClick={() => setAdding(true)}>
              <CIcon icon={cilPlus} className="me-1" />
              Add first team
            </CButton>
          </div>
        )
      ) : filteredTeams.length === 0 ? (
        <div className="text-medium-emphasis small text-center py-3">
          No teams match &ldquo;{search}&rdquo;.
        </div>
      ) : (
        <div className="list-group">
          {filteredTeams.map((team) => (
            <div key={team.id} className="list-group-item d-flex align-items-center gap-2 py-2">
              <TeamAvatar team={team} />
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-semibold text-truncate">{team.team_name}</div>
                <div className="text-medium-emphasis small">{team.sn}</div>
              </div>
              {newIds.includes(team.id) && (
                <CBadge color="info" shape="rounded-pill">
                  New
                </CBadge>
              )}
              {team.is_active === 1 ? (
                <CBadge color="success">Active</CBadge>
              ) : (
                <CBadge color="secondary">Inactive</CBadge>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 text-end">
        <Link to="/teams" className="small text-decoration-none">
          Manage all teams
          <CIcon icon={cilArrowRight} size="sm" className="ms-1" />
        </Link>
      </div>
    </div>
  )
}

export default GradeTeams
